import { useState } from "react";
import { Plus } from "lucide-react";
import { useTenant } from "@/lib/tenant-context.js";
import { useAsync } from "@/hooks/use-async.js";
import { useKeyboardShortcuts } from "@/hooks/use-keyboard-shortcuts.js";
import { Button } from "@/components/ui/button.js";
import { Skeleton } from "@/components/ui/skeleton.js";
import { Badge } from "@/components/ui/badge.js";
import { useToast } from "@/components/ui/toast.js";
import { DataTable } from "@/views/data-table.js";
import { RecurrenceEditor } from "@/views/recurrence-editor.js";
import { buildUpdatePayload } from "@/lib/recurrence.js";
import type { RecurrenceSeries } from "@/lib/types.js";

export function RecurrencesPage(): React.JSX.Element {
  const { api } = useTenant();
  const { toast } = useToast();
  const series = useAsync(() => api.listRecurrenceSeries(), [api]);
  const customers = useAsync(() => api.listCustomers(), [api]);
  const services = useAsync(() => api.listServices(), [api]);
  const resources = useAsync(() => api.listResources(), [api]);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<RecurrenceSeries | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const rows = series.data?.data ?? [];
  useKeyboardShortcuts({
    c: () => setCreating(true),
    j: () =>
      setSelectedId(rows.length === 0 ? null : pickNext(rows, selectedId, 1)),
    k: () =>
      setSelectedId(rows.length === 0 ? null : pickNext(rows, selectedId, -1)),
    e: () => {
      const row = rows.find((r) => r.id === selectedId);
      if (row) setEditing(row);
    }
  });

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-lg font-semibold">Recurrences</h1>
        <Button onClick={() => setCreating(true)}>
          <Plus className="mr-1 h-4 w-4" /> New series
        </Button>
      </div>
      {series.loading ? (
        <Skeleton className="h-72 w-full" />
      ) : (
        <DataTable<RecurrenceSeries>
          rows={rows}
          getRowKey={(r) => r.id}
          selectedRowKey={selectedId}
          onRowClick={(r) => setSelectedId(r.id)}
          columns={[
            { header: "RRULE", cell: (r) => <code className="text-xs">{r.rrule}</code> },
            { header: "Starts", cell: (r) => new Date(r.dtstart).toLocaleString() },
            {
              header: "Status",
              cell: (r) => (
                <Badge variant={r.cancelled_at ? "destructive" : "success"}>
                  {r.cancelled_at ? "cancelled" : "active"}
                </Badge>
              )
            },
            {
              header: "",
              cell: (r) => (
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={() => setEditing(r)}>
                    Edit
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={async () => {
                      try {
                        await api.cancelRecurrenceSeries(r.id);
                        toast({ title: "Cancelled series", variant: "success" });
                        series.reload();
                      } catch (error) {
                        toast({ title: "Could not cancel", description: String(error), variant: "destructive" });
                      }
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              )
            }
          ]}
        />
      )}

      <RecurrenceEditor
        open={creating}
        onOpenChange={setCreating}
        onSubmit={async ({ rrule, dtstart }) => {
          if (!rrule || !dtstart) {
            throw new Error("RRULE and start are required");
          }
          const customer = customers.data?.data?.[0];
          const service = services.data?.data?.[0];
          const resource = resources.data?.data?.[0];
          if (!customer || !service || !resource) {
            throw new Error("Need at least one customer, service, and resource configured");
          }
          await api.createRecurrenceSeries({
            rrule,
            dtstart,
            customer_id: customer.id,
            service_id: service.id,
            primary_resource_id: resource.id
          });
          toast({ title: "Series created", variant: "success" });
          series.reload();
        }}
      />

      <RecurrenceEditor
        open={editing !== null}
        onOpenChange={(v) => !v && setEditing(null)}
        isEdit
        initialRrule={editing?.rrule}
        initialDtstart={editing?.dtstart}
        // No instance id when launched from the series list — editor forces scope=all.
        onSubmit={async ({ scope, rrule, fromInstanceAppointmentId }) => {
          if (!editing) return;
          const payload = buildUpdatePayload({
            scope: scope ?? "all",
            rrule: rrule || undefined,
            fromInstanceAppointmentId
          });
          await api.updateRecurrenceSeries(editing.id, payload);
          toast({ title: "Series updated", variant: "success" });
          series.reload();
          setEditing(null);
        }}
      />
    </div>
  );
}

function pickNext<T extends { id: string }>(rows: T[], current: string | null, delta: 1 | -1): string {
  const idx = current ? rows.findIndex((r) => r.id === current) : -1;
  const nextIdx = idx === -1 ? (delta === 1 ? 0 : rows.length - 1) : Math.max(0, Math.min(rows.length - 1, idx + delta));
  return rows[nextIdx]!.id;
}
